/**
 * Named profile library in localStorage.
 *
 * One active configuration lives on the device (decision D9); this is the
 * browser-side library that lets you keep several and switch between them. It
 * exports and imports as a single JSON document so a set of patterns can be
 * version-controlled alongside the firmware.
 *
 * localStorage is synchronous and can throw (private mode, quota). Everything here
 * degrades to "the library is empty" rather than breaking the app: losing saved
 * profiles must never stop you configuring the device in front of you.
 */

import { toProfileFile, type ProfileFile } from '../pattern-editor/jsonIO.ts';
import type { Draft } from '../../domain/draft.ts';

const STORAGE_KEY = 'vimousejiggler.profiles.v1';
const RECORD_FORMAT = 'vimousejiggler.library';
const RECORD_VERSION = 1;

export interface StoredProfile extends ProfileFile {
  /**
   * Local-only: when this profile was last pushed to a device.
   *
   * `| undefined` rather than plain optional because `exactOptionalPropertyTypes`
   * is on: building the entry as `{ ...file, lastPushedAt: maybeUndefined }` is an
   * error otherwise.
   */
  lastPushedAt?: string | undefined;
}

interface LibraryRecord {
  format: typeof RECORD_FORMAT;
  version: number;
  profiles: StoredProfile[];
}

function emptyRecord(): LibraryRecord {
  return { format: RECORD_FORMAT, version: RECORD_VERSION, profiles: [] };
}

function storage(): Storage | null {
  try {
    // Access can throw outright in some privacy configurations.
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

export function isAvailable(): boolean {
  const s = storage();
  if (!s) return false;
  try {
    const probe = '__vmj_probe__';
    s.setItem(probe, '1');
    s.removeItem(probe);
    return true;
  } catch {
    return false;
  }
}

export function load(): StoredProfile[] {
  const s = storage();
  if (!s) return [];

  let text: string | null;
  try {
    text = s.getItem(STORAGE_KEY);
  } catch {
    return [];
  }
  if (!text) return [];

  try {
    const parsed: unknown = JSON.parse(text);
    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      (parsed as LibraryRecord).format !== RECORD_FORMAT ||
      !Array.isArray((parsed as LibraryRecord).profiles)
    ) {
      return [];
    }
    return (parsed as LibraryRecord).profiles;
  } catch {
    // Corrupt data: report empty rather than throwing. The user can still work.
    return [];
  }
}

function persist(profiles: StoredProfile[]): boolean {
  const s = storage();
  if (!s) return false;
  const record: LibraryRecord = { ...emptyRecord(), profiles };
  try {
    s.setItem(STORAGE_KEY, JSON.stringify(record));
    return true;
  } catch {
    // Almost always a quota error. The caller surfaces it; the app keeps working.
    return false;
  }
}

export function saveProfile(name: string, draft: Draft): StoredProfile[] {
  const trimmed = name.trim();
  if (!trimmed) return load();

  const profiles = load();
  const entry = toProfileFile(draft, trimmed) as StoredProfile;
  const index = profiles.findIndex((p) => p.name === trimmed);

  if (index >= 0) {
    const previous = profiles[index];
    profiles[index] = previous?.lastPushedAt
      ? { ...entry, lastPushedAt: previous.lastPushedAt }
      : entry;
  } else {
    profiles.push(entry);
  }

  persist(profiles);
  return load();
}

export function deleteProfile(name: string): StoredProfile[] {
  const profiles = load().filter((p) => p.name !== name);
  persist(profiles);
  return load();
}

export function renameProfile(from: string, to: string): StoredProfile[] {
  const target = to.trim();
  if (!target) return load();

  const profiles = load();
  const entry = profiles.find((p) => p.name === from);
  if (entry) entry.name = target;
  persist(profiles);
  return load();
}

export function markPushed(name: string, when = new Date()): StoredProfile[] {
  const profiles = load();
  const entry = profiles.find((p) => p.name === name);
  if (entry) entry.lastPushedAt = when.toISOString();
  persist(profiles);
  return load();
}

/** Everything, as one document, for export-all. */
export function exportLibrary(): string {
  const record: LibraryRecord = { ...emptyRecord(), profiles: load() };
  return `${JSON.stringify(record, null, 2)}\n`;
}

/**
 * Merge an exported library into the current one. Replaces same-named profiles
 * rather than duplicating them, so re-importing your own export is idempotent.
 */
export function importLibrary(text: string): { added: number; replaced: number } {
  const parsed: unknown = JSON.parse(text);
  if (typeof parsed !== 'object' || parsed === null) {
    throw new Error('The file must contain a JSON object.');
  }

  const candidates = Array.isArray(parsed)
    ? parsed
    : Array.isArray((parsed as LibraryRecord).profiles)
      ? (parsed as LibraryRecord).profiles
      : null;

  if (!candidates) {
    throw new Error(
      'Expected an exported library — an object with a "profiles" array, or an array of profiles.',
    );
  }

  const existing = load();
  let added = 0;
  let replaced = 0;

  for (const candidate of candidates) {
    if (typeof candidate !== 'object' || candidate === null) continue;
    const entry = candidate as StoredProfile;
    if (typeof entry.name !== 'string' || !entry.name.trim()) continue;
    if (typeof entry.config !== 'object' || entry.config === null) continue;
    if (!Array.isArray(entry.steps)) continue;

    const index = existing.findIndex((p) => p.name === entry.name);
    if (index >= 0) {
      existing[index] = entry;
      replaced++;
    } else {
      existing.push(entry);
      added++;
    }
  }

  if (!persist(existing)) {
    throw new Error('Could not write to browser storage — it may be full or disabled.');
  }
  return { added, replaced };
}

export function clearLibrary(): void {
  const s = storage();
  try {
    s?.removeItem(STORAGE_KEY);
  } catch {
    /* nothing useful to do */
  }
}
